const NUMBER = "([+-]?(?:\\d*\\.)?\\d+(?:e[+-]?\\d+)?)";

// Draw Edges creates M/L polylines. This parser stays deliberately strict so
// artwork curves are never flattened just because somebody clicked Edit.
export function parseEditableRailPath(d) {
  if (!d || /[CQASTHVZ]/i.test(d)) return null;
  const points = [];
  const re = new RegExp(`[ML]\\s*${NUMBER}[,\\s]+${NUMBER}`, "gi");
  let match;
  while ((match = re.exec(d)) !== null) {
    points.push({ x: Number(match[1]), y: Number(match[2]) });
  }
  return points.length >= 2 ? points : null;
}

export function editableRailPointsToD(points) {
  if (!points?.length) return "";
  return points
    .map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(2)},${point.y.toFixed(2)}`)
    .join(" ");
}

export function railNodesFromPoints(points) {
  return (points || []).map((point) => ({ x: point.x, y: point.y, handleIn: null, handleOut: null, smooth: false }));
}

export function railNodesToD(nodes) {
  if (!nodes?.length) return "";
  let d = `M${nodes[0].x.toFixed(2)},${nodes[0].y.toFixed(2)}`;
  for (let index = 1; index < nodes.length; index++) {
    const previous = nodes[index - 1];
    const node = nodes[index];
    if (previous.handleOut || node.handleIn) {
      const c1 = previous.handleOut || { x: previous.x, y: previous.y };
      const c2 = node.handleIn || { x: node.x, y: node.y };
      d += ` C${c1.x.toFixed(2)},${c1.y.toFixed(2)} ${c2.x.toFixed(2)},${c2.y.toFixed(2)} ${node.x.toFixed(2)},${node.y.toFixed(2)}`;
    } else {
      d += ` L${node.x.toFixed(2)},${node.y.toFixed(2)}`;
    }
  }
  return d;
}

export function smoothRailNodes(nodes) {
  if (!nodes?.length) return [];
  return nodes.map((node, index) => {
    const previous = nodes[Math.max(0, index - 1)];
    const next = nodes[Math.min(nodes.length - 1, index + 1)];
    const dx = (next.x - previous.x) / 6;
    const dy = (next.y - previous.y) / 6;
    return {
      ...node,
      handleIn: index === 0 ? null : { x: node.x - dx, y: node.y - dy },
      handleOut: index === nodes.length - 1 ? null : { x: node.x + dx, y: node.y + dy },
      smooth: true,
    };
  });
}

export function smoothRailNodeAt(nodes, nodeIndex) {
  if (!nodes?.[nodeIndex]) return nodes;
  const smoothed = smoothRailNodes(nodes);
  return nodes.map((node, index) => index === nodeIndex ? smoothed[index] : node);
}

export function cornerRailNodeAt(nodes, nodeIndex) {
  return (nodes || []).map((node, index) => index === nodeIndex
    ? { ...node, handleIn: null, handleOut: null, smooth: false }
    : node);
}

export function reverseRailNodes(nodes) {
  return (nodes || []).slice().reverse().map((node) => ({
    ...node,
    handleIn: node.handleOut ? { ...node.handleOut } : null,
    handleOut: node.handleIn ? { ...node.handleIn } : null,
  }));
}

export function reverseEditableRailPath(d) {
  const points = parseEditableRailPath(d);
  return points ? editableRailPointsToD(points.reverse()) : null;
}

export function simplifyRailPoints(points) {
  if (!points || points.length <= 24) return points || [];
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const diagonal = Math.hypot(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
  const toleranceSq = Math.pow(Math.max(0.15, diagonal * 0.002), 2);

  const distanceToSegmentSq = (point, start, end) => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    if (dx === 0 && dy === 0) return Math.pow(point.x - start.x, 2) + Math.pow(point.y - start.y, 2);
    const t = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy)));
    const x = start.x + t * dx;
    const y = start.y + t * dy;
    return Math.pow(point.x - x, 2) + Math.pow(point.y - y, 2);
  };

  const reduce = (start, end, keep) => {
    let furthest = -1;
    let furthestDistance = toleranceSq;
    for (let index = start + 1; index < end; index++) {
      const distance = distanceToSegmentSq(points[index], points[start], points[end]);
      if (distance > furthestDistance) {
        furthest = index;
        furthestDistance = distance;
      }
    }
    if (furthest < 0) return;
    keep.add(furthest);
    reduce(start, furthest, keep);
    reduce(furthest, end, keep);
  };

  const keep = new Set([0, points.length - 1]);
  reduce(0, points.length - 1, keep);
  return points.filter((_, index) => keep.has(index));
}
